import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { IMdBody } from '../../../types/shared';

import { validateForm } from '../../../utils/forms/formValidation';
import {
  CHECKBOX_ERROR,
  FORM_LABEL,
} from '../../../utils/forms/translationKeys';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { ITranslationItem, languageHelper } from '../../../utils/language';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, Label, LabelLight, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';
import { Theme } from '../../../styles/themes';

import ErrorMessage from '../../molecules/error-message/ErrorMessage';
import CheckIcon from '../icons/CheckIcon';
import InfoPopover from '../info-popover/InfoPopover';

interface Props {
  label: string;
  name: string;
  id: string;
  isRequired?: boolean;
  isChecked?: boolean;
  helperMessage?: string;
  onChange?: (name: string, isChecked: boolean) => void;
  tooltip?: IMdBody | null;
}

interface CheckedProps {
  isChecked: boolean;
  hasError?: boolean;
}

const ConsentCheckbox = ({
  label,
  name,
  id,
  isRequired,
  isChecked,
  helperMessage,
  onChange,
  tooltip,
}: Props) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(!!isChecked);
  const [errorMessage, setErrorMessage] = useState<ITranslationItem>();
  const { lang } = languageHelper();

  const handleErrorMessage = () => {
    if (checkboxRef?.current) {
      setErrorMessage(
        validateForm(checkboxRef.current.validity, label, CHECKBOX_ERROR)
      );
    }
  };

  const handleOnChange = () => {
    setChecked(!checked);

    if (onChange) {
      onChange(name, !checked);
    }
  };

  return (
    <div>
      <Container isChecked={checked} hasError={!!errorMessage}>
        <StyledLabel data-testid="consent-checkbox" htmlFor={id}>
          <CheckBoxWrapper>
            <Checkbox
              id={id}
              data-testid="hidden-checkbox"
              data-optiontype="single-option"
              name={name}
              required={isRequired}
              type="checkbox"
              checked={checked}
              value={label}
              onChange={() => {
                handleOnChange();
                handleErrorMessage();
              }}
              ref={checkboxRef}
              onInvalid={(e) => {
                e.preventDefault();
                handleErrorMessage();
              }}
            />
            <CustomCheckmark isChecked={checked}>
              <CheckboxIcon isChecked={checked} color={Colors.white} />
            </CustomCheckmark>
            {label && (
              <Body data-testid="label">
                <span>{label}</span>
                {!isRequired && (
                  <StyledLabelLight>
                    {FORM_LABEL.required[lang]}
                  </StyledLabelLight>
                )}
              </Body>
            )}
          </CheckBoxWrapper>
        </StyledLabel>
        {tooltip && (
          <StyledInfoPopover label={label}>
            <ReactMarkdownExtended>
              {tooltip.childMarkdownRemark?.rawMarkdownBody}
            </ReactMarkdownExtended>
          </StyledInfoPopover>
        )}
      </Container>
      {helperMessage && <StyledSmallText>{helperMessage}</StyledSmallText>}
      {errorMessage && (
        <StyledErrorMessage hasNegativeMargin={false}>
          {errorMessage[lang]}
        </StyledErrorMessage>
      )}
    </div>
  );
};

export default ConsentCheckbox;

const Container = styled.div<CheckedProps>`
  align-items: baseline;
  background-color: ${({ isChecked }) =>
    isChecked ? Colors.abnGreen50 : Colors.white};
  border: 1px solid
    ${({ isChecked, theme, hasError }) =>
      isChecked
        ? theme.colors.abnGreen50
        : hasError
        ? Colors.abnNegative400
        : Colors.abnNeutralsN65};
  border-radius: ${({ theme }) => theme.radiuses.S};
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${mq.from.breakpoint.XL`
    &:hover {
      background-color: ${Colors.abnGreen55};
      border: 1px solid ${({
        isChecked,
        theme,
      }: {
        isChecked: boolean;
        theme: Theme;
      }) => (isChecked ? theme.colors.abnGreen300 : Colors.abnGreen75)};
        
      input[type='checkbox']:not([checked='true'], :checked) ~ div:first-of-type {
          background: ${Colors.abnGreen55};
          border: 1px solid ${Colors.abnGreen75};
        }
    }
  `}

  &:active {
    background: ${Colors.abnGreen65};
    border: 1px solid ${({ theme }) => theme.colors.abnGreen100};

    input[type='checkbox']:not([checked='true'], :checked) ~ div:first-of-type {
      background: ${Colors.abnGreen65};
      border: 1px solid ${({ theme }) => theme.colors.abnGreen100};
    }
  }
`;

const StyledErrorMessage = styled(ErrorMessage)`
  min-height: 1em;
  width: 100%;
`;

const StyledLabel = styled(Label)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px 12px 12px 16px;
  width: 100%;

  ${mq.from.breakpoint.L`
    &:hover {
      cursor: pointer;
    }
  `}
`;

const CheckBoxWrapper = styled.div`
  align-items: center;
  color: ${Colors.abnBlack};
  cursor: pointer;
  display: flex;
  gap: 6px;
`;

const CustomCheckmark = styled.div<CheckedProps>`
  align-items: center;
  background: ${({ isChecked, theme }) =>
    isChecked ? theme.colors.abnGreen200 : Colors.white};
  border: 1px solid
    ${({ isChecked, theme }) =>
      isChecked ? theme.colors.abnGreen200 : Colors.abnNeutralsN100};
  border-radius: ${({ theme }) => theme.radiuses.S};
  box-sizing: border-box;
  display: flex;
  height: 16px;
  justify-content: center;
  margin-right: ${Spacings.XS};
  padding: 1px;
  width: 16px;
`;

const Checkbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 16px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 16px;
`;

const CheckboxIcon = styled(CheckIcon)<CheckedProps>`
  height: 16px;
  visibility: ${({ isChecked }) => (isChecked ? 'visible' : 'hidden')};
  width: 16px;
`;

const StyledInfoPopover = styled(InfoPopover)`
  margin-right: 12px;

  ${mq.from.breakpoint.M`
    margin-right: 20px;
  `}
`;

const StyledSmallText = styled(SmallText)`
  color: ${Colors.abnNeutral400};
  margin-top: ${Spacings.XS};
`;

const StyledLabelLight = styled(LabelLight)`
  color: ${Colors.abnNeutral300};
  margin-left: ${Spacings.XXS};
`;
